import { Link } from '@tanstack/react-router'
import { Film, ImageOff, TreePine, TriangleAlert } from 'lucide-react'
import { useState } from 'react'

import type { StageRow } from '@/api/stages'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/components/ui/utils'

import { FEATURE_INFO, featureLabel, flagLabel, placesState, presentFeatures, stageThumb, worldLabel } from './words'

/**
 * One background tile: the picture, its name and world, what the place offers (water, ground,
 * sky… as plain-word chips), anything that looks wrong (amber), "Off" when stories never pick it,
 * and whether its scenery is automatic or laid out by hand. A fixed slot model so a grid of 400
 * reads as one rhythm; the whole card opens the editor.
 */

const MAX_FEATURES = 4
const MAX_FLAGS = 2

export function BackgroundCard({ row }: { row: StageRow }) {
  const features = presentFeatures(row.features)
  const state = placesState(row.annotation)
  return (
    <li
      className={cn(
        'card relative flex min-w-0 flex-col overflow-hidden transition-shadow hover:shadow-pop focus-within:shadow-pop [content-visibility:auto] [contain-intrinsic-size:auto_300px]',
        !row.enabled && 'opacity-80',
      )}
      data-testid="background-card"
      data-uid={row.uid}
      data-enabled={row.enabled}
    >
      <div className="relative">
        <CardPicture src={stageThumb(row.uid, row)} name={row.name} />
        <div className="absolute left-2 top-2 flex flex-wrap gap-1">
          {!row.enabled ? (
            <Badge tone="neutral" className="bg-paper shadow-card" data-testid="off-badge">
              Off
            </Badge>
          ) : null}
          {row.kind === 'live' ? (
            <Badge tone="info" className="shadow-card">
              <Film className="size-3" aria-hidden />
              Moving picture
            </Badge>
          ) : null}
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3">
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold text-ink" title={row.name}>
            <Link to="/backgrounds/$" params={{ _splat: row.uid }} className="rounded-sm after:absolute after:inset-0 after:content-[''] focus-visible:focus-ring">
              {row.name}
            </Link>
          </h3>
          <p className="truncate text-xs text-ink-3">
            {worldLabel(row.world)}
            {row.catalog_world && row.catalog_world !== row.world ? ` · moved from ${worldLabel(row.catalog_world)}` : ''}
          </p>
        </div>

        <ul className="flex min-h-7 flex-wrap gap-1" aria-label="What this place has" data-testid="feature-chips">
          {features.slice(0, MAX_FEATURES).map((f) => {
            const Icon = FEATURE_INFO[f]?.icon
            return (
              <li key={f} className="inline-flex h-6 items-center gap-1 rounded-full border border-line bg-paper px-2 text-xs text-ink-2">
                {Icon ? <Icon className="size-3.5 text-ink-3" aria-hidden /> : null}
                {featureLabel(f)}
              </li>
            )
          })}
          {features.length > MAX_FEATURES ? <li className="self-center text-xs text-ink-3">+{features.length - MAX_FEATURES}</li> : null}
          {features.length === 0 ? <li className="self-center text-xs text-ink-3">No places marked yet</li> : null}
        </ul>

        {/* The server writes flags as sentences: the first one or two, clamped, the rest on hover. */}
        {row.flags.length ? (
          <ul className="flex flex-col gap-0.5" aria-label="Worth a look" title={row.flags.map(flagLabel).join('\n')}>
            {row.flags.slice(0, MAX_FLAGS).map((f) => (
              <li key={f} className="flex items-start gap-1 text-xs text-warn" data-testid="flag-badge">
                <TriangleAlert className="mt-0.5 size-3 shrink-0" aria-hidden />
                <span className="line-clamp-2">{flagLabel(f)}</span>
              </li>
            ))}
            {row.flags.length > MAX_FLAGS ? <li className="pl-4 text-xs text-warn">and {row.flags.length - MAX_FLAGS} more</li> : null}
          </ul>
        ) : null}

        <div className="mt-auto flex flex-wrap items-center gap-1">
          {row.annotation.lint_errors > 0 && row.flags.length === 0 ? (
            <Badge tone="danger" title={state.hint}>
              {state.label}
            </Badge>
          ) : null}
          <Badge tone={row.scenery.mode === 'custom' ? 'accent' : 'neutral'} className="ml-auto" data-testid="scenery-badge" title={row.scenery.mode === 'custom' ? 'Scenery laid out by hand' : 'Each story picks its own scenery'}>
            <TreePine className="size-3" aria-hidden />
            {row.scenery.mode === 'custom' ? `Custom · ${row.scenery.count}` : 'Auto'}
          </Badge>
        </div>
      </div>
    </li>
  )
}

function CardPicture({ src, name }: { src: string; name: string }) {
  const [broken, setBroken] = useState(false)
  return (
    <div className="relative flex aspect-video items-center justify-center overflow-hidden bg-paper-2">
      {!broken ? (
        <img src={src} alt="" loading="lazy" decoding="async" draggable={false} onError={() => setBroken(true)} className="size-full object-cover" />
      ) : (
        <span className="flex flex-col items-center gap-1 text-xs text-ink-3">
          <ImageOff className="size-6" aria-hidden />
          <span className="sr-only">No picture of {name}</span>
        </span>
      )}
    </div>
  )
}
